import React, { useEffect, useRef, useState } from "react";
import { getCategories, getSubCategories, getPriceRange, fetchFilteredProducts } from "../api";
import { useSearchParams } from "react-router-dom";
import ProductGrid from "./ProductGrid";
import LoadMoreButton from "../../../shared/ui/LoadMoreButton";
import { mergeProducts } from "../utils/productDisplay";
import NoResults from "../../../shared/ui/NoResults";
import Loading from "../../../shared/ui/Loading";
import Seo from "../../../shared/ui/Seo";
const ProductListing = () => {
  // /products?category=<id> (e.g. a home page category tile) opens with that category selected;
  // &sub=<id> (a header subcategory link) selects that subcategory within it instead.
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get("category");
  const subParam = searchParams.get("sub");
  const selectedParam = subParam || categoryParam;
  const initialCategories = selectedParam ? [selectedParam] : [];
  const initialExpanded = categoryParam ? [categoryParam] : [];
  const [selectedPredefinedRange, setSelectedPredefinedRange] = useState(null);
  const [customMin, setCustomMin] = useState("");
  const [customMax, setCustomMax] = useState("");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(500);
  const [isInitialized, setIsInitialized] = useState(false);
  const [selectedSubCategories, setSelectedSubCategories] = useState(
    () => new Set(initialCategories)
  );
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState({});
  const [expandedCategories, setExpandedCategories] = useState(
    () => new Set(initialExpanded)
  );
  const [sortOption, setSortOption] = useState("newArrivals");
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Responses for an older set of filters are ignored.
  const latestRequest = useRef(0);

  const predefinedRanges = {
    range1: [minPrice, 1000],
    range2: [1000, 2500],
    range3: [2500, 5000],
    range4: [5000, maxPrice],
  };

  const getPriceFilter = () => {
    let low, high;
    if (selectedPredefinedRange === "custom") {
      low = customMin ? parseFloat(customMin) : minPrice;
      high = customMax ? parseFloat(customMax) : maxPrice;
    } else if (
      selectedPredefinedRange &&
      predefinedRanges[selectedPredefinedRange]
    ) {
      [low, high] = predefinedRanges[selectedPredefinedRange];
    } else {
      low = minPrice;
      high = maxPrice;
    }
    return [low, high];
  };

  const isCustomValid =
    customMin !== "" &&
    customMax !== "" &&
    parseFloat(customMin) < parseFloat(customMax);

  const handlePredefinedChange = (e) => {
    setSelectedPredefinedRange(e.target.value);
  };

  const handleCustomMinChange = (e) => {
    setCustomMin(e.target.value);
  };

  const handleCustomMaxChange = (e) => {
    setCustomMax(e.target.value);
  };

  const handleSortChange = (e) => {
    const newSortOption = e.target.value;
    setSortOption(newSortOption);
    // Reset other filter options when sort changes.
    setSelectedPredefinedRange(null);
    setCustomMin("");
    setCustomMax("");
    setSelectedSubCategories(new Set());
    setCurrentPage(0);
    // Fetch products with default price filter and new sort option.
    //fetchProducts(0, minPrice, maxPrice, []);
  };

  const fetchProducts = async (page, low, high, selectedCategories = []) => {
    const request = ++latestRequest.current;
    // A new set of filters starts the list again from the first page.
    if (page === 0) setProducts([]);
    setLoading(true);
    try {
      const response = await fetchFilteredProducts(
        page,
        18,
        low,
        high,
        selectedCategories,
        sortOption // Pass the current sort option
      );
      if (request !== latestRequest.current) return;
      setTotalPages(response.totalPages);
      // setProducts((prevProducts) => {
      //   const updatedProducts = [...prevProducts, ...response.content];
      //   return Array.from(
      //     new Map(updatedProducts.map((p) => [p.id, p])).values()
      //   );
      // });
      setProducts((prev) => mergeProducts(prev, response.content, page));
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      if (request === latestRequest.current) setLoading(false);
    }
  };

  const fetchCategoriesData = async () => {
    try {
      const response = await getCategories();
      setCategories(response.content);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchSubCategoriesData = async (categoryId) => {
    try {
      if (!subCategories[categoryId]) {
        const response = await getSubCategories(categoryId);
        setSubCategories((prev) => ({ ...prev, [categoryId]: response }));
      }
    } catch (error) {
      console.error("Error fetching sub-categories:", error);
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        const data = await getPriceRange();
        setMinPrice(data.minPrice);
        setMaxPrice(data.maxPrice);
        await fetchProducts(0, data.minPrice, data.maxPrice, initialCategories);
        setIsInitialized(true);
      } catch (error) {
        console.error("Error fetching price range:", error);
      }
    };
    init();
    fetchCategoriesData();
    if (categoryParam) fetchSubCategoriesData(categoryParam);
  }, []);

  // useEffect(() => {
  //   const delayFetch = setTimeout(() => {
  //     if (currentPage !== 0) {
  //       setCurrentPage(0);
  //     }
  //     const [low, high] = getPriceFilter();
  //     fetchProducts(0, low, high, [...selectedSubCategories]);
  //   }, 500);

  //   return () => clearTimeout(delayFetch);
  // }, [
  //   selectedPredefinedRange,
  //   customMin,
  //   customMax,
  //   selectedSubCategories,
  //   minPrice,
  //   maxPrice,
  //   sortOption,
  // ]);

  const toggleCategory = (categoryId) => {
    setExpandedCategories((prev) => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(categoryId)) {
        newExpanded.delete(categoryId);
      } else {
        newExpanded.add(categoryId);
        fetchSubCategoriesData(categoryId);
      }
      return newExpanded;
    });
  };

  // One subcategory at a time: choosing one replaces the previous choice.
  const selectSubCategory = (subCategoryId) => {
    setSelectedSubCategories(new Set([subCategoryId]));
  };
  // Clicking the selected one again clears it.
  const clearSubCategoryIfSelected = (subCategoryId) => {
    if (selectedSubCategories.has(subCategoryId)) {
      setSelectedSubCategories(new Set());
    }
  };
  const loadMore = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    const [low, high] = getPriceFilter();
    fetchProducts(nextPage, low, high, [...selectedSubCategories]);
  };
  // const loadMoreProducts = () => {
  //   const nextPage = currentPage + 1;
  //   setCurrentPage(nextPage);
  //   const [low, high] = getPriceFilter();
  //   fetchProducts(nextPage, low, high, [...selectedSubCategories]);
  // };
  // useEffect(() => {
  //   const [low, high] = getPriceFilter();
  //   fetchProducts(currentPage, low, high, [...selectedSubCategories]);
  // }, [currentPage]);

  useEffect(() => {
    if (!isInitialized) return;
    if (selectedPredefinedRange === "custom") {
      // Don’t fetch until user clicks Apply
      return;
    }
    const delayFetch = setTimeout(() => {
      setCurrentPage(0);
      const [low, high] = getPriceFilter();
      fetchProducts(0, low, high, [...selectedSubCategories]);
    }, 500);

    return () => clearTimeout(delayFetch);
  }, [
    selectedPredefinedRange,
    //customMin,
    // customMax,
    selectedSubCategories,
    minPrice,
    maxPrice,
    sortOption,
  ]);

  // Another category link while already on this page: switch to that category.
  const appliedCategoryParam = useRef(`${categoryParam}|${subParam}`);
  useEffect(() => {
    const applied = `${categoryParam}|${subParam}`;
    if (appliedCategoryParam.current === applied) return;
    appliedCategoryParam.current = applied;
    setSelectedSubCategories(new Set(selectedParam ? [selectedParam] : []));
    if (categoryParam) {
      setExpandedCategories(new Set([categoryParam]));
      fetchSubCategoriesData(categoryParam);
    }
  }, [categoryParam, subParam]);

  const resetFilters = () => {
    if (categoryParam || subParam) setSearchParams({});
    setSelectedPredefinedRange(null);
    setCustomMin("");
    setCustomMax("");
    setSelectedSubCategories(new Set());
    setSortOption("newArrivals");
    setExpandedCategories(new Set());
    setSubCategories({});
    setCurrentPage(0);
  };
  const applyCustomPriceFilter = () => {
    // Reset to first page
    setCurrentPage(0);

    // Compute the low/high from the inputs (fallback to min/max)
    const low = customMin ? parseFloat(customMin) : minPrice;
    const high = customMax ? parseFloat(customMax) : maxPrice;

    // Trigger the fetch with the custom range and any selected categories
    fetchProducts(0, low, high, [...selectedSubCategories]);
  };

  // The products are sorted on the server side.
  const sortedProducts = products;

  // Below lg the filters open as a drawer; keep the page from scrolling behind it.
  useEffect(() => {
    document.body.classList.toggle("filter-drawer-open", filtersOpen);
    return () => document.body.classList.remove("filter-drawer-open");
  }, [filtersOpen]);

  const priceOptions = [
    ["range1", "Under LKR 1,000"],
    ["range2", "LKR 1,000 - 2,500"],
    ["range3", "LKR 2,500 - 5,000"],
    ["range4", "Over LKR 5,000"],
    ["custom", "Custom range"],
  ];
  const activeFilterCount =
    selectedSubCategories.size + (selectedPredefinedRange ? 1 : 0);

  const filterPanel = (
    <aside
      className={`filter-panel${filtersOpen ? " is-open" : ""}`}
      aria-label="Product filters"
    >
      <div className="filter-panel__header">
        <span className="filter-panel__heading">Filters</span>
        <div className="d-flex align-items-center gap-12">
          {activeFilterCount > 0 && (
            <button type="button" className="filter-panel__clear" onClick={resetFilters}>
              Clear all
            </button>
          )}
          <button
            type="button"
            className="filter-panel__close d-lg-none"
            onClick={() => setFiltersOpen(false)}
            aria-label="Close filters"
          >
            <i className="ph ph-x" />
          </button>
        </div>
      </div>

      <div className="filter-panel__body">
        <section className="filter-group">
          <h3 className="filter-group__title">Category</h3>
          <ul className="filter-categories">
            {categories.map((category) => {
              const expanded = expandedCategories.has(category.id);
              const selectedCount = (subCategories[category.id] || []).filter((sub) =>
                selectedSubCategories.has(sub.id)
              ).length;
              return (
                <li key={category.id} className="filter-category">
                  <button
                    type="button"
                    className={`filter-category__toggle${expanded ? " is-expanded" : ""}`}
                    onClick={() => toggleCategory(category.id)}
                    aria-expanded={expanded}
                  >
                    <span className="filter-category__name">{category.name}</span>
                    {selectedCount > 0 && (
                      <span className="filter-category__count">{selectedCount}</span>
                    )}
                    <i className="ph ph-caret-down filter-category__caret" aria-hidden="true" />
                  </button>
                  {expanded && (
                    <ul className="filter-category__subs">
                      {!subCategories[category.id] && (
                        <li className="filter-option text-gray-400">Loading...</li>
                      )}
                      {subCategories[category.id]?.length === 0 && (
                        <li className="filter-option text-gray-400">No subcategories</li>
                      )}
                      {subCategories[category.id]?.map((subCategory) => (
                        <li key={subCategory.id}>
                          <label className="filter-option checked-bigmall">
                            <input
                              type="radio"
                              name="subCategory"
                              className="form-check-input"
                              checked={selectedSubCategories.has(subCategory.id)}
                              onChange={() => selectSubCategory(subCategory.id)}
                              onClick={() => clearSubCategoryIfSelected(subCategory.id)}
                            />
                            <span>{subCategory.name}</span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </section>

        <section className="filter-group">
          <h3 className="filter-group__title">Price</h3>
          <ul className="filter-options checked-bigmall">
            {priceOptions.map(([value, label]) => (
              <li key={value}>
                <label className="filter-option">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="priceRange"
                    value={value}
                    checked={selectedPredefinedRange === value}
                    onChange={handlePredefinedChange}
                  />
                  <span>{label}</span>
                </label>
              </li>
            ))}
          </ul>
          {selectedPredefinedRange === "custom" && (
            <div className="filter-price">
              <div className="filter-price__inputs">
                <input
                  type="number"
                  min="0"
                  placeholder={`Min (${minPrice})`}
                  value={customMin}
                  onChange={handleCustomMinChange}
                  className="form-control"
                  aria-label="Minimum price"
                />
                <span className="text-gray-400">-</span>
                <input
                  type="number"
                  min="0"
                  placeholder={`Max (${maxPrice})`}
                  value={customMax}
                  onChange={handleCustomMaxChange}
                  className="form-control"
                  aria-label="Maximum price"
                />
              </div>
              {(customMin !== "" || customMax !== "") && !isCustomValid && (
                <small className="text-danger d-block mt-6">
                  Min price must be less than max price.
                </small>
              )}
              <button
                type="button"
                className="btn bg-main-600 text-white w-100 mt-10 py-8 text-sm"
                onClick={() => {
                  applyCustomPriceFilter();
                  setFiltersOpen(false);
                }}
                disabled={!isCustomValid}
              >
                Apply
              </button>
            </div>
          )}
        </section>
      </div>

      <div className="filter-panel__footer d-lg-none">
        <button
          type="button"
          className="btn bg-main-600 text-white w-100 py-12"
          onClick={() => setFiltersOpen(false)}
        >
          Show results
        </button>
      </div>
    </aside>
  );

  // A category link (/products?category=…) is its own landing page for search engines.
  const categoryName = categories.find((category) => category.id === categoryParam)?.name;
  const subCategoryName = (subCategories[categoryParam] || []).find(
    (subCategory) => subCategory.id === subParam
  )?.name;
  const listingName = subCategoryName || categoryName;
  const listingPath = categoryParam
    ? `/products?category=${categoryParam}${subParam ? `&sub=${subParam}` : ""}`
    : "/products";

  return (
    <div className="container container-lg pt-20">
      <Seo
        title={listingName || "All Products"}
        description={
          listingName
            ? `Shop ${listingName} online at Bigmall. Best prices in Sri Lanka with island-wide delivery and cash on delivery.`
            : "Browse all products at Bigmall - electronics, home appliances, beauty, fashion and more at the best prices in Sri Lanka."
        }
        path={listingPath}
      />
      <h1 className="visually-hidden">{listingName || "All Products"}</h1>
      <div className="row gx-4">
        <div className="col-lg-3">
          {filterPanel}
          {filtersOpen && (
            <div
              className="filter-panel__backdrop d-lg-none"
              onClick={() => setFiltersOpen(false)}
              aria-hidden="true"
            />
          )}
        </div>
        <div className="col-lg-9">
          <div className="listing-toolbar">
            <button
              type="button"
              className="listing-toolbar__filters d-lg-none"
              onClick={() => setFiltersOpen(true)}
            >
              <i className="ph ph-sliders-horizontal" />
              Filters
              {activeFilterCount > 0 && (
                <span className="filter-category__count">{activeFilterCount}</span>
              )}
            </button>
            <label className="listing-toolbar__sort">
              <span className="text-gray-500 d-none d-sm-inline">Sort by:</span>
              <select
                value={sortOption}
                onChange={handleSortChange}
                className="form-select w-auto"
              >
                <option value="priceLow">Price Low to High</option>
                <option value="priceHigh">Price High to Low</option>
                <option value="newArrivals">New Arrivals</option>
              </select>
            </label>
          </div>

          {loading && products.length === 0 && (
            <Loading message="Loading products..." />
          )}

          {!loading && products.length === 0 && (
            <NoResults message="No products found for the selected filters." />
          )}

          {products.length > 0 && (
            <>
              <ProductGrid
                products={sortedProducts}
                currentPage={currentPage}
                totalPages={totalPages}
                showLoadmore={false}
                bare
              />
              <LoadMoreButton
                hasMore={currentPage < totalPages - 1}
                loading={loading}
                onClick={loadMore}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductListing;
